import { useMemo } from 'react';
import { diffLines } from 'diff';
import { cn } from '../../lib/utils';

function linesFromChange(change: { value: string; added?: boolean; removed?: boolean }): string[] {
  const v = change.value.replace(/\r\n/g, '\n');
  if (v === '') return [];
  const body = v.endsWith('\n') ? v.slice(0, -1) : v;
  return body.split('\n');
}

export type YamlDiffRow = {
  left: string;
  right: string;
  leftNum: number | null;
  rightNum: number | null;
  leftTone: 'neutral' | 'removed';
  rightTone: 'neutral' | 'added';
};

/** Build aligned rows for a GitHub-style split diff (single scroll). */
export function buildYamlSideBySideRows(before: string, after: string): YamlDiffRow[] {
  const changes = diffLines(before ?? '', after ?? '');
  const rows: YamlDiffRow[] = [];
  let leftNum = 1;
  let rightNum = 1;

  for (const ch of changes) {
    const lines = linesFromChange(ch);
    if (ch.added) {
      for (const line of lines) {
        rows.push({
          left: '',
          right: line,
          leftNum: null,
          rightNum: rightNum++,
          leftTone: 'neutral',
          rightTone: 'added',
        });
      }
    } else if (ch.removed) {
      for (const line of lines) {
        rows.push({
          left: line,
          right: '',
          leftNum: leftNum++,
          rightNum: null,
          leftTone: 'removed',
          rightTone: 'neutral',
        });
      }
    } else {
      for (const line of lines) {
        rows.push({
          left: line,
          right: line,
          leftNum: leftNum++,
          rightNum: rightNum++,
          leftTone: 'neutral',
          rightTone: 'neutral',
        });
      }
    }
  }

  return rows;
}

export function YamlSideBySideDiff({
  before,
  after,
  className,
}: {
  before: string;
  after: string;
  className?: string;
}) {
  const rows = useMemo(() => buildYamlSideBySideRows(before, after), [before, after]);

  return (
    <div
      className={cn(
        'overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-slate-950 text-xs',
        className,
      )}
    >
      <table className="w-full border-collapse table-fixed">
        <thead className="sticky top-0 z-10 bg-slate-900 text-slate-400 text-[10px] uppercase tracking-wide">
          <tr>
            <th className="w-10 px-1 py-1.5 text-right font-medium border-b border-r border-slate-700">Ln</th>
            <th className="w-[calc(50%-2.5rem)] px-2 py-1.5 text-left font-medium border-b border-r border-slate-700">
              Current
            </th>
            <th className="w-10 px-1 py-1.5 text-right font-medium border-b border-r border-slate-700">Ln</th>
            <th className="w-[calc(50%-2.5rem)] px-2 py-1.5 text-left font-medium border-b border-slate-700">
              Draft
            </th>
          </tr>
        </thead>
        <tbody className="font-mono leading-snug text-[11px]">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                No differences (or both empty).
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i} className="border-b border-slate-800/80">
                <td className="align-top py-0.5 pr-1 text-right text-slate-500 select-none border-r border-slate-800">
                  {row.leftNum ?? ''}
                </td>
                <td
                  className={cn(
                    'align-top py-0.5 px-2 whitespace-pre-wrap break-all border-r border-slate-800',
                    row.leftTone === 'removed' && 'bg-red-950/70 text-red-100',
                    row.leftTone === 'neutral' && row.left === '' && 'bg-slate-900/50',
                    row.leftTone === 'neutral' && row.left !== '' && 'text-slate-300',
                  )}
                >
                  {row.left || '\u00a0'}
                </td>
                <td className="align-top py-0.5 pr-1 text-right text-slate-500 select-none border-r border-slate-800">
                  {row.rightNum ?? ''}
                </td>
                <td
                  className={cn(
                    'align-top py-0.5 px-2 whitespace-pre-wrap break-all',
                    row.rightTone === 'added' && 'bg-emerald-950/70 text-emerald-100',
                    row.rightTone === 'neutral' && row.right === '' && 'bg-slate-900/50',
                    row.rightTone === 'neutral' && row.right !== '' && 'text-slate-300',
                  )}
                >
                  {row.right || '\u00a0'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
